/**
 * Hero Section Mockups - 3 Design Options
 * Option 1: Calm-Inspired "The Zen Marketer"
 * Option 2: Ahrefs-Inspired "The Powerhouse"
 * Option 3: Hybrid "The Future Flow"
 */

import React, { useState, useEffect, useRef } from 'react';

// Option 1: Calm-Inspired Hero - "The Zen Marketer"
const CalmHero = ({ onGetStarted }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section 
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 50%, #FFF7ED 100%)'
      }}
      data-testid="hero-calm"
    >
      {/* Soft floating shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-100/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Badge */}
        <div 
          className={`inline-flex items-center gap-2 mb-8 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-slate-200/50 shadow-sm transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <span className="text-orange-500 text-lg">✨</span>
          <span className="text-slate-600 font-light italic" style={{ fontFamily: 'Georgia, serif' }}>
            Where creativity meets calm
          </span>
        </div>

        {/* Headline */}
        <h1 
          className={`text-5xl md:text-7xl lg:text-8xl text-slate-800 mb-8 leading-[1.05] tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Marketing,{' '}
          <span className="italic text-slate-600">reimagined</span>
          <br />
          <span className="text-orange-500">peacefully.</span>
        </h1>

        {/* Subheadline */}
        <p 
          className={`text-xl md:text-2xl text-slate-500 max-w-2xl mx-auto mb-12 font-light leading-relaxed transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          Let AI handle the chaos. Create stunning ads in 60 seconds 
          while you focus on what matters most.
        </p>

        {/* CTA Buttons */}
        <div 
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <button 
            onClick={onGetStarted}
            className="group px-8 py-4 bg-slate-800 text-white rounded-full font-light text-lg shadow-xl hover:shadow-2xl hover:bg-slate-900 hover:-translate-y-1 transition-all duration-300"
            style={{ fontFamily: 'Georgia, serif' }}
            data-testid="hero-calm-cta"
          >
            Begin your journey
            <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
          </button>
          <button className="px-8 py-4 text-slate-600 font-light hover:text-slate-800 transition-colors">
            Watch the magic
          </button>
        </div>

        {/* Video/Demo Showcase - Floating Glass Card */}
        <div 
          className={`relative max-w-4xl mx-auto transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl border border-white/50 shadow-2xl p-3 hover:scale-[1.02] transition-transform duration-700">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                <div className="w-3 h-3 rounded-full bg-green-400/60" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 bg-slate-50 rounded-full text-xs text-slate-400">
                  adelevate.ai
                </div>
              </div>
            </div>
            
            {/* Demo content placeholder */}
            <div className="relative aspect-video bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8 text-orange-500 ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <p className="text-slate-500 font-light italic" style={{ fontFamily: 'Georgia, serif' }}>
                    See the magic unfold
                  </p>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-4 right-4 px-3 py-1 bg-white/80 backdrop-blur-sm rounded-full text-xs text-slate-500">
                60 seconds
              </div>
            </div>
          </div>
          
          {/* Floating stats */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-8 px-8 py-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-100">
            <div className="text-center">
              <div className="text-2xl font-light text-slate-800">10K+</div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Happy minds</div>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div className="text-center">
              <div className="text-2xl font-light text-slate-800">99%</div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Time saved</div>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div className="text-center">
              <div className="text-2xl font-light text-slate-800">∞</div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">Possibilities</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Option 2: Ahrefs-Inspired Hero - "The Powerhouse"
const AhrefsHero = ({ onGetStarted }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section 
      className="relative min-h-screen flex items-center bg-white"
      data-testid="hero-ahrefs"
    >
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: Content */}
          <div className="lg:col-span-5">
            {/* Trust badge */}
            <div 
              className={`inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-md transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            >
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
              <span className="text-sm font-semibold text-orange-700">
                Trusted by 10,000+ marketers
              </span>
            </div>

            {/* Headline */}
            <h1 
              className={`text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 leading-[1.1] tracking-tight transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              Stop wasting time.
              <br />
              <span className="text-orange-500">Start converting.</span>
            </h1>

            {/* Subheadline */}
            <p 
              className={`text-lg text-slate-600 mb-8 leading-relaxed transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            >
              AdElevate generates high-converting ads in <strong>60 seconds</strong>. 
              No designers. No copywriters. No waiting.
              <span className="block mt-2 text-orange-600 font-semibold">Save $4,950/month vs agencies.</span>
            </p>

            {/* CTA Row */}
            <div 
              className={`flex flex-wrap items-center gap-4 mb-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            >
              <button 
                onClick={onGetStarted}
                className="group px-6 py-3.5 bg-orange-500 text-white font-bold uppercase tracking-wider text-sm rounded-md hover:bg-orange-600 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transition-all"
                data-testid="hero-ahrefs-cta"
              >
                Start Free Trial
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </button>
              <button className="px-6 py-3.5 border-2 border-slate-200 text-slate-700 font-semibold rounded-md hover:border-slate-300 hover:bg-slate-50 transition-all">
                See Pricing
              </button>
            </div>

            {/* Micro stats */}
            <div 
              className={`flex items-center gap-6 pt-6 border-t border-slate-100 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['👨‍💼', '👩‍💻', '👨‍🎨', '👩‍💼'].map((emoji, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-sm">
                      {emoji}
                    </div>
                  ))}
                </div>
                <span className="text-sm text-slate-500">
                  <strong className="text-slate-700">2,341</strong> joined this week
                </span>
              </div>
            </div>
          </div>

          {/* Right: Demo */}
          <div 
            className={`lg:col-span-7 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div className="relative">
              {/* Browser mockup */}
              <div className="bg-slate-900 rounded-xl shadow-2xl overflow-hidden border border-slate-700">
                {/* Browser top bar */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 rounded-md max-w-md mx-auto">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-sm text-slate-400">app.adelevate.ai/dashboard</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    </svg>
                  </div>
                </div>
                
                {/* Demo content */}
                <div className="aspect-[16/10] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                  {/* Simulated dashboard UI */}
                  <div className="absolute inset-4 grid grid-cols-3 gap-4">
                    <div className="col-span-2 bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                      <div className="flex items-center justify-between mb-4">
                        <div className="h-3 w-32 bg-slate-700 rounded" />
                        <div className="h-3 w-20 bg-orange-500/30 rounded" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} className="aspect-video bg-slate-700/50 rounded-md flex items-center justify-center border border-slate-600/30">
                            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                              <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                        <div className="h-2 w-16 bg-slate-700 rounded mb-3" />
                        <div className="text-3xl font-bold text-white">$12.4K</div>
                        <div className="text-xs text-green-400 mt-1">↑ 24% this month</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                        <div className="h-2 w-20 bg-slate-700 rounded mb-3" />
                        <div className="text-3xl font-bold text-white">847</div>
                        <div className="text-xs text-orange-400 mt-1">Ads generated</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Live indicator */}
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <span className="text-xs font-medium text-green-400">Live Demo</span>
                  </div>
                </div>
              </div>

              {/* Floating feature cards */}
              <div className="absolute -left-4 top-1/4 px-4 py-3 bg-white rounded-lg shadow-xl border border-slate-100 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">⚡</span>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">60 Second Ads</div>
                    <div className="text-xs text-slate-500">Ready to publish</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 px-4 py-3 bg-white rounded-lg shadow-xl border border-slate-100 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🎯</span>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">99% Cost Savings</div>
                    <div className="text-xs text-slate-500">vs traditional agencies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom trust bar */}
      <div className="absolute bottom-0 inset-x-0 py-6 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
            <span className="text-sm text-slate-500 font-medium">Trusted by teams at</span>
            {['Startup', 'Agency', 'Enterprise', 'E-commerce', 'SaaS'].map(name => (
              <span key={name} className="text-lg font-bold text-slate-400">{name}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Option 3: Hybrid Hero - "The Future Flow"
const HybridHero = ({ onGetStarted }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePosition({ x, y });
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0B1120 0%, #1E293B 50%, #0F172A 100%)' }}
      data-testid="hero-hybrid"
    >
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[100px] animate-pulse"
          style={{ animationDuration: '8s' }}
        />
        <div 
          className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px] animate-pulse"
          style={{ animationDuration: '6s', animationDelay: '2s' }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-[120px]"
        />
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            {/* Badge */}
            <div 
              className={`inline-flex items-center gap-3 mb-8 px-4 py-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-full transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
              <span className="text-sm text-slate-400 font-mono">
                v2.0 — Now with AI Video Generation
              </span>
            </div>

            {/* Headline */}
            <h1 
              className={`text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-[1.05] tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-slate-400">
                From Frustrated
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500">
                to Phenomenal
              </span>
              <br />
              <span className="text-slate-500 text-4xl md:text-5xl font-light">
                in 60 seconds.
              </span>
            </h1>

            {/* Subheadline with accent border */}
            <p 
              className={`text-lg text-slate-400 mb-10 pl-6 border-l-2 border-orange-500 leading-relaxed font-mono transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              AI-powered marketing that creates stunning images, 
              videos, and copy. No expertise required.
              <span className="block mt-2 text-orange-400">
                99% cost savings. 100% your brand.
              </span>
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-wrap items-center gap-4 mb-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <button 
                onClick={onGetStarted}
                className="group relative px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl shadow-[0_0_30px_rgba(249,115,22,0.4)] hover:shadow-[0_0_50px_rgba(249,115,22,0.6)] transition-all duration-300 border border-orange-400/30 overflow-hidden"
                data-testid="hero-hybrid-cta"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Creating Free
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-amber-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button className="px-8 py-4 text-slate-400 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 hover:text-white hover:border-slate-600 transition-all">
                Watch Demo
              </button>
            </div>

            {/* Stats row */}
            <div 
              className={`flex items-center gap-8 pt-8 border-t border-slate-800 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              {[
                { value: '10K+', label: 'Users' },
                { value: '1M+', label: 'Ads Created' },
                { value: '99%', label: 'Time Saved' }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-white font-mono">{stat.value}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: 3D Tilt Card Demo */}
          <div 
            className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div 
              ref={cardRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
              className="relative perspective-1000"
              style={{
                transform: `rotateY(${mousePosition.x * 10}deg) rotateX(${-mousePosition.y * 10}deg)`,
                transition: 'transform 0.1s ease-out'
              }}
            >
              {/* Glow effect */}
              <div 
                className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 via-purple-500/20 to-blue-500/20 rounded-3xl blur-xl opacity-50"
                style={{
                  transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`
                }}
              />
              
              {/* Card */}
              <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">A</span>
                    </div>
                    <span className="text-white font-medium">AdElevate Studio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-xs text-slate-400">Live</span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="aspect-video p-6 relative">
                  {/* Simulated video/demo content */}
                  <div className="absolute inset-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center border border-slate-700/30">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
                        <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p className="text-slate-400 text-sm">See AdElevate in action</p>
                    </div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-10 right-10 px-3 py-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                    <span className="text-green-400 text-xs font-mono">+847 ads today</span>
                  </div>
                  
                  <div className="absolute bottom-10 left-10 px-3 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                    <span className="text-orange-400 text-xs font-mono">60s generation</span>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-800/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-2">
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-800 flex items-center justify-center text-xs text-slate-400">
                            {i}K
                          </div>
                        ))}
                      </div>
                      <span className="text-sm text-slate-500">Active users</span>
                    </div>
                    <button className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors">
                      Try Free
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-500/30 rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${2 + Math.random() * 4}s`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>
    </section>
  );
};

// Main Component with Variant Switcher
const HeroMockups = ({ onGetStarted, initialVariant = 'hybrid' }) => {
  const [activeVariant, setActiveVariant] = useState(initialVariant);

  const variants = [
    { id: 'calm', name: 'Calm-Inspired', emoji: '🧘', description: 'Serene & Minimal' },
    { id: 'ahrefs', name: 'Ahrefs-Inspired', emoji: '🚀', description: 'Bold & Data-Driven' },
    { id: 'hybrid', name: 'Hybrid', emoji: '✨', description: 'Dark & Premium' }
  ];

  return (
    <div className="relative">
      {/* Variant Switcher */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-2 py-2 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-full shadow-2xl border border-slate-200 dark:border-slate-700">
        <span className="px-3 text-sm font-medium text-slate-500 dark:text-slate-400">Preview:</span>
        {variants.map(variant => (
          <button
            key={variant.id}
            onClick={() => setActiveVariant(variant.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeVariant === variant.id
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            data-testid={`hero-switch-${variant.id}`}
          >
            <span>{variant.emoji}</span>
            <span className="hidden sm:inline">{variant.name}</span>
          </button>
        ))}
      </div>

      {/* Render Active Hero */}
      {activeVariant === 'calm' && <CalmHero onGetStarted={onGetStarted} />}
      {activeVariant === 'ahrefs' && <AhrefsHero onGetStarted={onGetStarted} />}
      {activeVariant === 'hybrid' && <HybridHero onGetStarted={onGetStarted} />}

      {/* Info Panel */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-xl shadow-xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-4">
          <span className="text-2xl">{variants.find(v => v.id === activeVariant)?.emoji}</span>
          <div>
            <div className="font-semibold text-slate-800 dark:text-white">
              {variants.find(v => v.id === activeVariant)?.name}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {variants.find(v => v.id === activeVariant)?.description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroMockups;
export { CalmHero, AhrefsHero, HybridHero };
